import type { OfferSiteBundle } from "./offerSite";

const stack = [
  "Kubernetes, Helm, Rancher, Docker",
  "AWS (EKS, RDS, S3, Shield), Hetzner bare-metal",
  "Terraform, Ansible, GitLab CI",
  "Prometheus, Grafana, Loki, ELK",
  "Cloudflare WAF/DDoS, Vault, PCI DSS",
  "PostgreSQL, MongoDB, Ceph, RabbitMQ",
  "Ollama / vLLM, RAG (ChromaDB) — по запросу",
];

const proof = [
  { brand: "Raiffeisen Bank", note: "AWS, PCI, audit CI/CD" },
  { brand: "Crypto exchange", note: "EKS, WAF, ноды, vaults" },
  { brand: "Epicenter / 27.ua", note: "Highload, Ceph, кластер" },
  { brand: "DDoS", note: "100 Gbps / кейс ~10M pps" },
];

/** Живой украинский: как пишут в UA IT, без кальки с русского. */
export const uk: OfferSiteBundle = {
  seoHome: {
    title: "Інфраструктура для обміну, платежів і highload | IT-24.pro",
    description:
      "Інженер інфраструктури: DDoS, PCI DSS, Kubernetes, ноди, vaults. Банки й біржі в досвіді. Telegram, USDT/UAH/інвойс. Retainer від $1 500/міс.",
  },
  ui: { open: "Відкрити", forWhom: "Для кого", includes: "Що входить" },
  nav: {
    services: "Послуги",
    cases: "Кейси",
    about: "Про мене",
    contacts: "Контакти",
    telegramCta: "Написати в Telegram",
  },
  hero: {
    eyebrow: "IT-24.pro · обмін · платежі · highload",
    h1: "Інфраструктура, на якій простій б’є по грошах",
    lead:
      "Збираю і тримаю production для бірж, платежів і сервісів під навантаженням: DDoS, PCI DSS, Kubernetes, секрети й ноди. Працюю сам — відповідаю в Telegram.",
    ctaTg: "Написати в Telegram",
    ctaOffers: "Послуги та ціни",
    chips: ["PCI DSS L1/L2", "DDoS 100+ Gbps", "USDT / інвойс PL", "З 2010 в інфраструктурі"],
  },
  proofTitle: "Досвід по іменах",
  proofLead: "Те, що можна звірити з резюме і кейсами — без «500+ проєктів».",
  proof: proof,
  offersTitle: "Послуги",
  offersLead: "Чотири формати. Без VPS і тарифів «адмін за $80».",
  offers: [
    {
      id: "incident",
      href: "/services/incident",
      title: "Термінова допомога",
      price: "$100–150/год · пакет від $600",
      blurb: "Піднімаю production після збою, DDoS або втрати доступу.",
      bullets: ["Реакція в Telegram", "Розбір після інциденту", "Без абонентського договору"],
    },
    {
      id: "audit",
      href: "/services/audit",
      title: "Аудит 1–2 дні",
      price: "$300–600",
      blurb: "Ризики інфраструктури, готовність до PCI DSS і стійкість до атак.",
      bullets: ["Письмовий звіт", "Що робити першим", "Дзвінок 30–60 хв"],
    },
    {
      id: "retainer",
      href: "/services/retainer",
      title: "Retainer — супровід інфраструктури",
      price: "$1 500–2 500/міс",
      blurb: "Зовнішній DevOps для обміну й фінтеху: релізи, моніторинг, інциденти.",
      bullets: ["40–80 год еквівалента", "Канал у Telegram", "Бекапи з перевіркою відновлення"],
    },
    {
      id: "projects",
      href: "/services/projects",
      title: "Проєкт під ключ",
      price: "$3 000–8 000 fixed",
      blurb: "Контур PCI DSS, захист від DDoS, міграція, Kubernetes і CI/CD, ноди й сховища секретів.",
      bullets: ["2–4 тижні", "Фіксована ціна", "Документація передачі"],
    },
  ],
  payTitle: "Як платять",
  payItems: ["USDT (TRC-20)", "UAH (карта / ФОП)", "Wise / Payoneer", "Польський інвойс з VAT"],
  payNote: "Юрособа в Польщі — зручно, якщо є клієнти в ЄС, і для розрахунків у USDT.",
  howTitle: "Як починаємо",
  howSteps: [
    { t: "Повідомлення в Telegram", p: "Коротко: що горить, стек, бюджет." },
    { t: "Дзвінок 30 хвилин", p: "Розберемо: інцидент, аудит, retainer чи разовий проєкт." },
    { t: "Аудит або пілот", p: "Невеликий вхід — без річного контракту «наосліп»." },
    { t: "Retainer або fixed", p: "Далі — супровід або проєкт з зрозумілим результатом." },
  ],
  aboutTeaser: {
    title: "Ігор Косицин",
    text: "DevOps / MLOps, Konin (PL). Linux highload з 2010-х, cloud-native останні роки. Юрособа в Польщі.",
    link: "Детальніше",
  },
  footerTagline:
    "Інфраструктура для обміну, фінтеху і високонавантажених систем. Ринок СНД · юрособа PL · Telegram.",
  footerEuNote: "Для ринку EU — окремий сайт",
  footerEuLink: "nexops.eu",
  casesPage: {
    seo: {
      title: "Кейси: банк, біржі, DDoS, highload | IT-24.pro",
      description: "Приклади: PCI DSS, інфраструктура бірж, захист від атак, e-commerce highload.",
    },
    h1: "Кейси",
    lead: "Задача, що зробив, чим закінчилось.",
    labels: { problem: "Задача", did: "Рішення", result: "Результат" },
    items: [
      {
        title: "Криптобіржа: production і комплаєнс",
        problem: "Потрібна була інфраструктура обміну й нод без ручних релізів і з нормальним контролем секретів.",
        did: "AWS EKS і bare-metal Kubernetes, Helm, GitLab CI, WAF, Ceph, ноди, vaults, Ansible і Terraform.",
        result: "Релізи без ручної рутини, контур під аудит, супровід у production.",
      },
      {
        title: "Raiffeisen: міграція і PCI DSS",
        problem: "Перенесення інфраструктури й вимоги фінсектору до аудиту та доступів.",
        did: "Міграція в AWS, Kubernetes, hardening CI/CD, участь у підготовці до PCI DSS Level 1/2 з боку інфраструктури.",
        result: "Готовність до перевірок і відтворювані деплої.",
      },
      {
        title: "DDoS під час рекламного запуску",
        problem: "Пік до ~10 млн пакетів на секунду; дата-центри не відсікали атаку.",
        did: "Фільтрація L3–L7, перенесення критичних сервісів на захищений контур, моніторинг, резервні маршрути.",
        result: "Близько 80% аудиторії зберігали доступ протягом місяця атаки.",
      },
      {
        title: "E-commerce / ритейл, highload",
        problem: "Піки навантаження, сховище на сотні терабайтів, доступ з різних регіонів.",
        did: "Nginx, MySQL, Ceph, кластери, кеш, схема Україна — Франція.",
        result: "Стабільна робота під навантаженням і захищене зберігання даних.",
      },
    ],
  },
  aboutPage: {
    seo: {
      title: "Про мене — Ігор Косицин, DevOps/MLOps | IT-24.pro",
      description: "Інженер інфраструктури для обміну й фінтеху. Польща, банки й біржі, PCI DSS, DDoS, Kubernetes.",
    },
    h1: "Про мене",
    lead: "Ігор Косицин. DevOps / MLOps: інфраструктуру проєктую і супроводжую сам, без «команди з двадцяти» на сайті.",
    body: [
      "Юрособа в Польщі (Konin). Раніше збирав і вів інфраструктуру в Україні — банки, ритейл, біржі.",
      "Беру задачі, де простій або злам б’ють по грошах і перевірках: платежі, обмін, пікові навантаження, підготовка до PCI DSS.",
      "Клієнтів з ЄС веду на nexops.eu. IT-24.pro — для замовників з СНД.",
    ],
    stackTitle: "Стек",
    stack: stack,
  },
  contactsPage: {
    seo: {
      title: "Контакти — Telegram, оплата USDT | IT-24.pro",
      description: "Напишіть у Telegram @devops_it24. USDT, UAH, польський інвойс.",
    },
    h1: "Контакти",
    lead: "Основний канал — Telegram. Відповідаю сам, без черги тікетів.",
    tgHint: "Коротко: стек і що зараз болить.",
    payTitle: "Оплата",
    payItems: ["USDT TRC-20", "UAH", "Wise / Payoneer", "Інвойс PL (VAT)"],
    payNote: "Юрособа в Польщі — зручно для бізнесу з клієнтами в ЄС і для розрахунків у USDT.",
  },
  services: {
    incident: {
      id: "incident",
      path: "/services/incident",
      seo: {
        title: "Термінова допомога з інфраструктури — інциденти і DDoS | IT-24.pro",
        description:
          "Відновлення production після атаки, компрометації або падіння сервісу. $100–150/год або пакет від $600. Telegram.",
      },
      breadcrumb: "Термінова допомога",
      h1: "Термінова допомога",
      lead: "Якщо production лежить — спочатку піднімаю доступність, потім розбираю причину. Без вибору «пакета Growth» посеред інциденту.",
      price: "$100–150/год · пакет інциденту від $600",
      forWhom: [
        "Обмін, платежі й сервіси з рекламним бюджетом",
        "Немає свого DevOps on-call",
        "Атака, компрометація, втрата доступу, загроза даним",
      ],
      includes: [
        "Діагностика і відновлення доступності",
        "Первинний hardening (доступ, firewall, очевидні дірки)",
        "Короткий post-mortem: що сталось і що лагодити далі",
        "За домовленістю — перехід до аудиту або retainer",
      ],
      cta: "Написати в Telegram про інцидент",
    },
    audit: {
      id: "audit",
      path: "/services/audit",
      seo: {
        title: "Аудит інфраструктури і готовності до PCI DSS | IT-24.pro",
        description: "Аудит 1–2 дні: безпека, контур DDoS, рахунок хмари, PCI DSS. $300–600.",
      },
      breadcrumb: "Аудит",
      h1: "Аудит 1–2 дні",
      lead: "Платите за ясність по ризиках — без річного контракту «наосліп».",
      price: "$300–600 fixed",
      forWhom: [
        "Підготовка до PCI DSS або зовнішнього аудиту",
        "Росте рахунок у хмарного провайдера",
        "Неясно, як контур тримає атаку або пік",
      ],
      includes: [
        "Огляд архітектури, доступів, бекапів, моніторингу",
        "Список ризиків за пріоритетом",
        "Оцінка бюджету на усунення (retainer або проєкт)",
        "Дзвінок по звіту",
      ],
      cta: "Замовити аудит у Telegram",
    },
    retainer: {
      id: "retainer",
      path: "/services/retainer",
      seo: {
        title: "DevOps retainer для обміну й фінтеху | IT-24.pro",
        description:
          "Абонентський супровід $1 500–2 500/міс: релізи, моніторинг, інциденти, бекапи.",
      },
      breadcrumb: "Retainer",
      h1: "Retainer — супровід інфраструктури",
      lead: "Порівнюйте не з хостингом за $100, а з Middle/Senior DevOps за $2.7–5k/міс з відпустками і bus factor.",
      price: "$1 500–2 500 / місяць",
      forWhom: [
        "Обмін, платежі, фінтех або highload-продукт",
        "Студія без свого DevOps на клієнтських проєктах",
        "Потрібен відповідальний контакт у Telegram, а не тікетна служба",
      ],
      includes: [
        "Моніторинг і реакція на інциденти в узгодженому вікні",
        "Релізи, CI/CD і правки інфраструктури в ліміті годин",
        "Оновлення, бекапи, періодична перевірка відновлення",
        "Короткий звіт раз на місяць: зроблене і зони ризику",
      ],
      cta: "Обговорити retainer у Telegram",
    },
    projects: {
      id: "projects",
      path: "/services/projects",
      seo: {
        title: "PCI DSS, DDoS, Kubernetes — проєкти під ключ | IT-24.pro",
        description:
          "Фіксована ціна $3–8k: контур PCI DSS, захист від DDoS, міграція, Kubernetes і CI/CD, ноди й vaults.",
      },
      breadcrumb: "Проєкти",
      h1: "Проєкт під ключ",
      lead: "Коли потрібен зрозумілий результат за 2–4 тижні, а не оплата «годин на місяць».",
      price: "$3 000–8 000 fixed · 2–4 тижні",
      forWhom: [
        "Підготовка інфраструктури до PCI DSS",
        "Контур DDoS / WAF після інциденту або перед запуском",
        "Міграція (хмара ↔ bare-metal), Kubernetes з нуля, ноди й vaults",
      ],
      includes: [
        "Скоп і фіксована ціна до старту",
        "IaC, документація, передача доступів",
        "Критерії приймання (доступність, пайплайн, відновлення з бекапа)",
        "Опція: супровід після запуску (retainer)",
      ],
      cta: "Описати проєкт у Telegram",
    },
  },
};
